import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Headphones, 
  Shield, 
  RefreshCw, 
  Wrench, 
  Users, 
  MapPin,
  CheckCircle,
  Clock,
  Award
} from 'lucide-react';
import { companyInfo } from '../mock';

export const ServicesSection = () => {
  const services = [
    {
      icon: Headphones,
      title: "Support Technique Expert",
      description: "Équipe technique qualifiée disponible pour vous accompagner dans vos projets",
      features: ["Conseil personnalisé", "Dimensionnement", "Assistance à distance"]
    },
    {
      icon: Shield,
      title: "Garantie Complète",
      description: "Garantie pièces et main d'œuvre pour une tranquillité d'esprit totale",
      features: ["10 ans sur batteries", "Support inclus", "Remplacement rapide"]
    },
    {
      icon: RefreshCw,
      title: "Remplacement Sous Conditions",
      description: "Service de remplacement efficace selon les conditions de garantie",
      features: ["Évaluation gratuite", "Remplacement rapide", "Livraison incluse"]
    },
    {
      icon: Wrench,
      title: "Installation Professionnelle",
      description: "Installation et mise en service par nos techniciens certifiés",
      features: ["Techniciens certifiés", "Mise en service", "Formation utilisateur"]
    }
  ];

  const zones = [
    {
      icon: MapPin,
      title: "France - Région Parisienne",
      location: "Moisenay, Seine-et-Marne",
      description: "Siège social et centre technique principal",
      services: ["Installation", "Maintenance", "Support technique"]
    },
    {
      icon: MapPin,
      title: "Mali - Afrique de l'Ouest",
      location: "Bamako, Mali",
      description: "Bureau commercial pour l'Afrique",
      services: ["Vente", "Support local", "Logistique"]
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-green-100 text-green-800 px-4 py-2">
            Nos Services
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Un Accompagnement
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              De A à Z
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            De la conception à la maintenance, notre équipe vous accompagne à chaque étape 
            de votre projet énergétique avec expertise et professionnalisme.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {services.map((service, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:scale-105 bg-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-lg font-bold text-gray-900">
                  {service.title}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {service.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Zones de Service */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Zones d'Intervention
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {zones.map((zone, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 bg-white border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <zone.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                        {zone.title}
                      </CardTitle>
                      <p className="text-gray-600 font-medium mb-2">{zone.location}</p>
                      <CardDescription className="text-gray-600">
                        {zone.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {zone.services.map((service, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-700">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Contact rapide */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border mb-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-yellow-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Distributeur et Représentant Officiel EXCLUSIF
            </h3>
            <div className="bg-gradient-to-r from-yellow-50 to-gray-50 rounded-xl p-6 mb-6 border border-yellow-200">
              <p className="text-lg font-semibold text-yellow-800 mb-2">POWMR - MALI</p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Z&N Solar</strong> est le seul distributeur et représentant officiel exclusif 
                de la marque PowMr au Mali, certifié par Hehejin Industrial Co., Ltd (Shenzhen).
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                <Shield className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-gray-700">Certification Officielle</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                <Award className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-gray-700">Exclusivité Mali</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                <CheckCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-gray-700">Garantie Constructeur</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact rapide original */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Besoin d'un Conseil ?
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Notre équipe technique est à votre disposition pour vous conseiller 
              et vous accompagner dans le choix de la solution PowMr la plus adaptée à vos besoins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center space-x-2 text-gray-700">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span>Réponse sous 24h</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <Users className="w-5 h-5 text-yellow-600" />
                <span>Experts certifiés PowMr</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};